import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getTripBooking } from '../../../api/trip-bookings/get-trip-booking'
import { updateTripBookingAllocations } from '../../../api/trip-bookings/update-trip-booking-allocations'
import { TripDateProperties } from '../../../lib/types/models/trip-booking/properties.trip-date'
import { twoButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { ItemNotFound } from '../../elements/item-not-found'
import LoadingScreen from '../../elements/loading/loading-screen'
import { TripBookingPlacesForm } from './trip-booking-places-form'

type Props = {
  tripBookingId: number
  onCancel: () => void
}

export function TripBookingAllocations({ tripBookingId, onCancel }: Props) {
  const queryClient = useQueryClient()
  const { data: tripBooking, isPending } = useQuery({
    queryKey: ['trip-booking', tripBookingId],
    queryFn: () => getTripBooking(tripBookingId, 'LIGHT')
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateTripBookingAllocations,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Les places ont été mises à jour avec succès')
      queryClient.invalidateQueries({
        queryKey: ['trip-booking', 'trip-place-to-center-requests', tripBookingId, 'PARTIAL']
      })
      onCancel()
    }
  })

  const tripBookingDetails = TripDateProperties.getTripBookingDetails(tripBooking, {
    showCost: false,
    displayDate: true,
    displayLabel: true
  })

  if (isPending) return <LoadingScreen />
  if (!tripBooking) return <ItemNotFound title="Réservation non trouvée" />
  return (
    <div>
      <div className={'flex flex-col gap-3 p-5'}>
        <div className="bg-white-hover border border-grey-light rounded flex flex-row gap-1 p-1">
          <Typography
            variant="tag"
            className="whitespace-nowrap"
            color={tailwindColors.grey.lighter}
          >
            Réservation :
          </Typography>
          <Typography variant="tag" color={tailwindColors.primary.DEFAULT}>
            {tripBookingDetails?.mainDetails}
          </Typography>
        </div>
      </div>

      <TripBookingPlacesForm
        data={tripBooking}
        buttonsConfig={twoButtonsConfig({
          cancelText: 'Annuler',
          confirmText: 'Valider',
          onCancel,
          onConfirm: async ({ data }) => await mutateAsync({ id: tripBookingId, data })
        })}
      />
    </div>
  )
}
